// @flow
import cloneDeep from 'lodash/cloneDeep';

type ContentModule = {
  id: string,
  imageCard: {}, // We don't need to know the contents of image card here.
  position: number | string,
};

type SafeContentModule = {
  id: string,
  imageCard: {}, // We don't need to know the contents of image card here.
  position: number,
};

type ContentModules = Array<ContentModule>;

type Item = {}; // For this file we don't care what the contents of Item is.
type Items = Array<Item>;

type ItemsAndContentModules = Array<ContentModule | Item>;

/**
 * Sort Content Modules by their position (ascending)
 *
 * @param {Object} a
 * @param {Object} b
 * @returns {number}
 */
const sortByPosition = (a: SafeContentModule, b: SafeContentModule): number =>
  a.position - b.position;

/**
 * If position is a string, then cast it as an int.
 *
 * @param {Object} contentModule
 * @returns {{id: string, imageCard: {}, position: number}}
 */
const ensurePositionIsValid = (
  contentModule: ContentModule
): SafeContentModule => ({
  id: contentModule.id,
  imageCard: contentModule.imageCard,
  position: +contentModule.position,
});

/**
 * Attach the content modules to the correct positions in the product listings.
 *
 * @param {Array} items
 * @param {Array} contentModules
 * @returns {Array}
 */
export default function combine(
  items: Items,
  contentModules: ContentModules
): ItemsAndContentModules {
  const sortedContentModules: Array<SafeContentModule> = cloneDeep(
    contentModules
  )
    .map(ensurePositionIsValid)
    .sort(sortByPosition);

  const combinedItems = [...items];
  sortedContentModules.forEach(({ imageCard, position }: SafeContentModule) => {
    if (position > combinedItems.length) {
      // If the content module has been positioned in a invalid position, then just put it at the end of the stack.
      combinedItems.push(imageCard);
    } else {
      const index = position - 1; // Arrays are 0 indexed. Position in Contentful starts at 1.
      combinedItems.splice(index, 0, imageCard);
    }
  });

  return combinedItems;
}
